import type { ReactNode } from 'react'

/**
 * A small Markdown renderer for comment bodies (APM-10). Claude Code writes
 * headings, lists, tables and fenced code; without this they arrived as one
 * unbroken block of text.
 *
 * It builds React elements directly and never touches innerHTML, so nothing in
 * a comment can inject markup, and hrefs are filtered to the schemes below.
 * The subset is the one people actually type: headings, emphasis, inline and
 * fenced code, bullet and numbered lists (nested), blockquotes, GFM tables,
 * links, rules. Reference links and raw HTML are shown as the literal text.
 *
 * Mentions keep the `@[Display Name](uuid)` shape the composer writes and the
 * API stores; they are matched before links so a mention never becomes one.
 *
 * Images (`![alt](src)`) render only when src is one of APM's own attachment
 * URLs, so a comment can never make a reader's browser fetch from elsewhere
 * (and tell that site who looked). Any other image is shown as a link.
 */

const MENTION = String.raw`@\[(?<mention>[^\]\n]{1,200})\]\((?<mid>[0-9a-fA-F-]{36})\)`
const INLINE = new RegExp([
  String.raw`\\(?<esc>[\\\`*_~\[\]()>#+\-.!|])`,
  String.raw`(?<fence>\`+)(?<code>[^]*?)\k<fence>`,
  MENTION,
  String.raw`!\[(?<ialt>[^\]\n]*)\]\((?<isrc>[^()\s]*)\)`,
  String.raw`\[(?<ltext>[^\]\n]*)\]\((?<lhref>[^()\s]*)\)`,
  String.raw`\*\*(?<strong>[^]+?)\*\*`,
  String.raw`__(?<strongb>[^]+?)__`,
  String.raw`(?<![\w*])\*(?<em>[^*\n]+?)\*(?![\w*])`,
  String.raw`(?<![\w_])_(?<emb>[^_\n]+?)_(?![\w_])`,
  String.raw`~~(?<del>[^]+?)~~`,
  String.raw`(?<auto>https?://[^\s<>()\[\]]+[^\s<>()\[\].,;:!?])`,
].join('|'), 'g')

/** Only these go into an href; anything else (javascript:, data:) stays as text. */
const safeHref = (u: string) => (/^(https?:\/\/|mailto:|\/|#)/i.test(u.trim()) ? u.trim() : null)

function link(href: string, body: ReactNode, key: string) {
  const safe = safeHref(href)
  if (!safe) return <span key={key}>{body}</span>
  const external = /^https?:\/\//i.test(safe)
  return <a key={key} href={safe} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{body}</a>
}

/** An image the comment may show: a file attached in APM itself. */
const ATTACHMENT_SRC = /^\/api\/projects\/[0-9a-f-]{36}\/attachments\/[0-9a-f-]{36}$/i

function image(src: string, alt: string, key: string) {
  if (!ATTACHMENT_SRC.test(src.trim())) return link(src, alt || src, key)
  return (
    <a key={key} className="md-imglink" href={src.trim()} target="_blank" rel="noopener">
      <img className="md-img" src={src.trim()} alt={alt} loading="lazy" />
    </a>
  )
}

/** Emphasis, code, links and mention chips inside one run of text. */
function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let n = 0
  for (const m of text.matchAll(INLINE)) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const g = m.groups!
    const k = `${key}i${n++}`
    if (g.esc !== undefined) out.push(g.esc)
    else if (g.code !== undefined) out.push(<code key={k}>{g.code.replace(/^ (.*) $/s, '$1')}</code>)
    else if (g.mention !== undefined) out.push(<span className="mention" key={k}>@{g.mention}</span>)
    else if (g.isrc !== undefined) out.push(image(g.isrc, g.ialt ?? '', k))
    else if (g.ltext !== undefined) out.push(link(g.lhref ?? '', inline(g.ltext, k), k))
    else if (g.strong !== undefined || g.strongb !== undefined) out.push(<strong key={k}>{inline((g.strong ?? g.strongb)!, k)}</strong>)
    else if (g.em !== undefined || g.emb !== undefined) out.push(<em key={k}>{inline((g.em ?? g.emb)!, k)}</em>)
    else if (g.del !== undefined) out.push(<del key={k}>{inline(g.del, k)}</del>)
    else if (g.auto !== undefined) out.push(link(g.auto, g.auto, k))
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** A paragraph's own newlines are line breaks: that is what people mean when they press Enter. */
function softBreaks(text: string, key: string): ReactNode[] {
  return text.split('\n').flatMap((l, i) => (i === 0 ? inline(l, `${key}l${i}`) : [<br key={`${key}br${i}`} />, ...inline(l, `${key}l${i}`)]))
}

const HEADING = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/
const FENCE = /^( {0,3})(`{3,}|~{3,})(.*)$/
const RULE = /^ {0,3}([-*_])\s*(?:\1\s*){2,}$/
const QUOTE = /^ {0,3}>\s?/
const ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/
const CELL_SEP = /^:?-+:?$/

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
const isTableSep = (line: string | undefined) => {
  if (line === undefined || !line.includes('-')) return false
  const c = cells(line)
  return c.length > 0 && c.every((x) => CELL_SEP.test(x))
}
const align = (spec: string) => (spec.startsWith(':') && spec.endsWith(':') ? 'center' : spec.endsWith(':') ? 'right' : spec.startsWith(':') ? 'left' : undefined)

/** Starts a block that interrupts a paragraph, so `text` then `- item` still makes a list. */
const startsBlock = (line: string) => HEADING.test(line) || FENCE.test(line) || RULE.test(line) || QUOTE.test(line) || ITEM.test(line)

function blocks(src: string, key: string): ReactNode[] {
  const lines = src.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n')
  const out: ReactNode[] = []
  let i = 0
  let n = 0
  const k = () => `${key}b${n++}`

  while (i < lines.length) {
    const line = lines[i]!
    if (!line.trim()) { i++; continue }

    const fence = FENCE.exec(line)
    if (fence) {
      const close = new RegExp(`^ {0,3}${fence[2]![0] === '`' ? '`' : '~'}{${fence[2]!.length},}\\s*$`)
      const body: string[] = []
      for (i++; i < lines.length && !close.test(lines[i]!); i++) body.push(lines[i]!.slice(fence[1]!.length))
      i++
      const lang = fence[3]!.trim().split(/\s+/)[0]
      out.push(<pre key={k()} {...(lang ? { 'data-lang': lang } : {})}><code>{body.join('\n')}</code></pre>)
      continue
    }

    if (RULE.test(line)) { out.push(<hr key={k()} />); i++; continue }

    const h = HEADING.exec(line)
    if (h) {
      const Tag = `h${h[1]!.length}` as 'h1'
      const id = k()
      out.push(<Tag key={id}>{inline(h[2]!, id)}</Tag>)
      i++
      continue
    }

    if (QUOTE.test(line)) {
      const body: string[] = []
      for (; i < lines.length && (QUOTE.test(lines[i]!) || (lines[i]!.trim() && !startsBlock(lines[i]!))); i++) body.push(lines[i]!.replace(QUOTE, ''))
      const id = k()
      out.push(<blockquote key={id}>{blocks(body.join('\n'), id)}</blockquote>)
      continue
    }

    if (line.includes('|') && isTableSep(lines[i + 1])) {
      const head = cells(line)
      const spec = cells(lines[i + 1]!).map(align)
      const rows: string[][] = []
      for (i += 2; i < lines.length && lines[i]!.includes('|') && lines[i]!.trim(); i++) rows.push(cells(lines[i]!))
      const id = k()
      out.push(
        <table key={id}>
          <thead><tr>{head.map((c, x) => <th key={x} style={{ textAlign: spec[x] ?? 'left' }}>{inline(c, `${id}h${x}`)}</th>)}</tr></thead>
          <tbody>{rows.map((r, y) => (
            <tr key={y}>{head.map((_, x) => <td key={x} style={{ textAlign: spec[x] ?? 'left' }}>{inline(r[x] ?? '', `${id}r${y}c${x}`)}</td>)}</tr>
          ))}</tbody>
        </table>,
      )
      continue
    }

    const first = ITEM.exec(line)
    if (first) {
      const indent = first[1]!.length
      const ordered = /\d/.test(first[2]!)
      const items: string[] = []
      while (i < lines.length) {
        const m = ITEM.exec(lines[i]!)
        // A shallower item ends this list; a deeper one was taken as part of the item above.
        if (!m || m[1]!.length < indent) break
        if (/\d/.test(m[2]!) !== ordered) break
        const body = [m[3]!]
        for (i++; i < lines.length; i++) {
          const l = lines[i]!
          if (!l.trim()) { body.push(''); continue }
          if (ITEM.test(l) && (ITEM.exec(l)![1]!.length <= indent)) break
          if (l.length - l.trimStart().length <= indent && startsBlock(l)) break
          body.push(l.slice(Math.min(indent + 2, l.length - l.trimStart().length)))
        }
        while (body.length && !body[body.length - 1]!.trim()) body.pop()
        items.push(body.join('\n'))
      }
      const id = k()
      const List = ordered ? 'ol' : 'ul'
      const start = ordered ? Number(first[2]!.slice(0, -1)) : 1
      out.push(
        <List key={id} {...(ordered && start !== 1 ? { start } : {})}>
          {items.map((it, x) => <li key={x}>{blocks(it, `${id}i${x}`)}</li>)}
        </List>,
      )
      continue
    }

    const para: string[] = []
    for (; i < lines.length && lines[i]!.trim(); i++) {
      if (para.length && startsBlock(lines[i]!)) break
      para.push(lines[i]!)
    }
    const id = k()
    out.push(<p key={id}>{softBreaks(para.join('\n'), id)}</p>)
  }
  return out
}

/** Render `text` as Markdown. `className` is added next to `md`. */
export function Markdown({ text, className }: { text: string; className?: string }) {
  return <div className={className ? `md ${className}` : 'md'}>{blocks(text, 'm')}</div>
}
